// src/pages/InjectionKey.tsx
import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../axiosInstance";
import ReactPaginate from "react-paginate";

import {
  IconFileImport,
  IconFileExport,
  IconFileExcel,
  IconPlus,
  IconChevronRight,
  IconChevronLeft,
  IconSearch,
  IconDownload,
  IconTrash,
} from "@tabler/icons-react";
import {
  Box,
  Button,
  Flex,
  LoadingOverlay,
  Menu,
  Select,
  Table,
  TableThead,
  TextInput,
  useMantineTheme,
  Badge,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useModal } from "../contexts/useModal";
import {
  ErrorAlert,
  TerminalsExcelImport,
  TerminalView,
  TerminalDelete,
  TerminalUpdate,
} from "../components/Modal";
import { setActionLog, INT_MAX } from "../utils/util";
import { useDynamicScroll } from "../utils/useDynamicScroll";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { ROW_HOVER_BG, BUTTON_WORD } from "../constants/colors";
import { useCanWrite } from "../utils/useCanWrite";

const Terminals: React.FC<{ menuTitle: string }> = ({ menuTitle }) => {
  const canWrite = useCanWrite();
  const [data, setData] = useState<any[]>([]);
  const [tab, setTab] = useState("ALL");
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  //const [showModal, setShowModal] = useState(false);

  const [total, setTotal] = useState(0);
  const [length, setLength] = useState(20);
  const [pageNo, setPageNo] = useState(1);
  const [pageTotal, setPageTotal] = useState(1);

  const _isSearch = useRef(false);
  const [isSearch, setIsSearch] = useState(false);
  const todayStr = dayjs().format("YYYY-MM-DD"); // KST 기준 오늘 날짜
  const [startDate, setStartDate] = useState<string>(todayStr);
  const [endDate, setEndDate] = useState<string>(todayStr);
  const [deviceType, setDeviceType] = useState("");
  const [vanType, setVanType] = useState("");
  const [searchSpSerialNo, setSearchSpSerialNo] = useState("");
  const [searchTerminalColor, setSearchTerminalColor] = useState("");
  const [searchFirmwareType, setSearchFirmwareType] = useState("");
  // const [selectSerialCode, setSelectSerialCode] = useState("025QHSC01");
  const [deviceTypeOptions, setDeviceTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [vanTypeOptions, setVanTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const { showModal } = useModal();
  const theme = useMantineTheme();
  const lock1 = useRef(false);
  const lock2 = useRef(false);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [importGroupId, setImportGroupId] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState<number>(0);

  // 동적 스크롤 제어 (최소 높이 500px)
  useDynamicScroll({ minHeight: 500 });

  // Excel import progress polling (persists after modal close)
  useEffect(() => {
    let polling: number | null = null;
    const startPolling = (groupId: string) => {
      if (polling) return;
      polling = window.setInterval(async () => {
        try {
          const resp = await axiosInstance.get(
            `/api/v1/terminal/imports/${groupId}`
          );
          const d = resp.data?.data || {};
          const percent = d.progress ?? 1;
          setImportProgress(percent);
          if (d.status === "FAILED") {
            try {
              localStorage.removeItem("terminalImportGroupId");
            } catch {}
            setImportGroupId(null);
            setImportProgress(0);
            if (polling) {
              clearInterval(polling);
              polling = null;
            }
            return;
          }
          if (percent >= 100) {
            try {
              localStorage.removeItem("terminalImportGroupId");
            } catch {}
            setImportGroupId(null);
            setImportProgress(100);
            if (polling) {
              clearInterval(polling);
              polling = null;
            }
          }
        } catch {
          try {
            localStorage.removeItem("terminalImportGroupId");
          } catch {}
          setImportGroupId(null);
          setImportProgress(0);
          if (polling) {
            clearInterval(polling);
            polling = null;
          }
        }
      }, 1000);
    };

    const existing = (() => {
      try {
        return localStorage.getItem("terminalImportGroupId");
      } catch {
        return null;
      }
    })();
    if (existing) {
      setImportGroupId(existing);
      startPolling(existing);
    }

    const probe = window.setInterval(() => {
      const gid = (() => {
        try {
          return localStorage.getItem("terminalImportGroupId");
        } catch {
          return null;
        }
      })();
      if (gid && gid !== importGroupId) {
        setImportGroupId(gid);
        startPolling(gid);
      }
    }, 1500);

    return () => {
      if (polling) clearInterval(polling);
      clearInterval(probe);
    };
  }, [importGroupId]);

  useEffect(() => {
    axiosInstance
      .get("/api/v1/common/device")
      .then((res) => {
        const mapped = res.data.data.map((item: any) => ({
          value: item.code,
          label: item.name,
        }));

        const withDefault = [{ value: "", label: "전체" }, ...mapped];
        setDeviceTypeOptions(withDefault);
        // 첫 번째 항목을 기본값으로 설정
        if (withDefault.length > 0) {
          setDeviceType(withDefault[0].value);
        }
      })
      .catch((err) => {
        console.log(err);
        const defaultOptions = [{ value: "", label: "전체" }];
        setDeviceTypeOptions(defaultOptions);
        setDeviceType(defaultOptions[0].value);
      });
    axiosInstance
      .get("/api/v1/common/van")
      .then((res) => {
        const mapped = res.data.data.map((item: any) => ({
          value: item.code,
          label: item.name,
        }));

        const withDefault = [{ value: "", label: "전체" }, ...mapped];
        setVanTypeOptions(withDefault);
        // 첫 번째 항목을 기본값으로 설정
        if (withDefault.length > 0) {
          setVanType(withDefault[0].value);
        }
      })
      .catch((err) => {
        console.log(err);
        const defaultOptions = [{ value: "", label: "전체" }];
        setVanTypeOptions(defaultOptions);
        setVanType(defaultOptions[0].value);
      });
  }, []);

  useEffect(() => {
    if (!lock1.current) {
      lock1.current = true;
      return;
    }
    if (pageNo !== 1) {
      setPageNo(1);
    } else {
      getList();
    }
  }, [length]);

  useEffect(() => {
    if (!lock2.current) {
      lock2.current = true;
      return;
    }
    getList();
  }, [pageNo]);

  useEffect(() => {
    if (tab === "ALL") {
      getList();
    } else {
      getList(tab);
    }
  }, [tab]);

  const getList = (terminalStatusParam?: string | null) => {
    if (_isSearch.current) return;
    _isSearch.current = true;
    setIsSearch(true);
    axiosInstance
      .get("/api/v1/terminal/list", {
        params: {
          currentPageNo: pageNo,
          recordsPerPage: length,
          ...(deviceType ? { deviceType } : {}),
          ...(vanType ? { vanType } : {}),
          ...(searchTerminalColor ? { terminalColor: searchTerminalColor } : {}),
          ...(searchFirmwareType
            ? { firmwareType: Number(searchFirmwareType) }
            : {}),
          // ...(selectSerialCode ? { selectSerialCode } : {}),
          spSerialNo: searchSpSerialNo.trim().toUpperCase() || undefined,
          ...(terminalStatusParam
            ? { ...(terminalStatusParam !== "ALL" ? { terminalStatus: terminalStatusParam } : {}) }
            : { ...(tab !== "ALL" ? { terminalStatus: tab } : {}) }),
        },
      })
      .then((res) => {
        if (res.status === 200) {
          const data = res.data as any;
          const r = data.data;
          const p = r.paging;
          setData(r.list || []);
          setTotal(p.totalRecordCount);
          setPageTotal(Math.ceil(p.totalRecordCount / length));
        } else {
          setData([]);
        }
      })
      .catch((err) => {
        console.error("단말기 리스트 로드 실패", err);
        // 인터셉터가 401 → refresh → 실패 → 로그인 이동 처리함
      })
      .finally(() => {
        _isSearch.current = false;
        setIsSearch(false);
      });
  };

  type TerminalSampleExportType = "register" | "incoming";

  const handleExport = async (type: TerminalSampleExportType) => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 업로드 샘플 다운로드 버튼 클릭",
    });
    try {
      const url =
        type === "register"
          ? "/files/register_sample.xlsx"
          : "/files/incoming_sample.xlsx";
      const timestamp = dayjs().format("YYYYMMDDHHmmss");
      const downloadName =
        type === "register"
          ? `등록_샘플_${timestamp}.xlsx`
          : `반품_샘플_${timestamp}.xlsx`;
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      await setActionLog({
        eventType: "EXPORT",
        title: "단말기 업로드 샘플 다운로드 성공",
      });
    } catch (err: any) {
      await setActionLog({
        eventType: "EXPORT",
        title: "단말기 업로드 샘플 다운로드 실패",
        content: err,
      });
      toast.error("엑셀 다운로드에 실패하였습니다.");
    }
  };

  const handleExcelImport = async () => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 정보 업로드 버튼 클릭",
    });
    const flag = await showModal({
      component: TerminalsExcelImport,
      props: { initialTab: "excel", initialMode: "register" },
    });
    if (flag) {
      await setActionLog({
        eventType: "IMPORT",
        title: "단말기 정보 업로드 성공",
      });
      toast.success("업로드가 완료되었습니다.");
      getList();
    }
  };

  const handleDirectRegister = async () => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 직접 등록 버튼 클릭",
    });
    const flag = await showModal({
      component: TerminalsExcelImport,
      props: { initialTab: "typing", initialMode: "register" },
    });
    if (flag) {
      await setActionLog({
        eventType: "IMPORT",
        title: "단말기 직접 등록 성공",
      });
      toast.success("등록이 완료되었습니다.");
      getList();
    }
  };

  const handleExcelExport = async () => {
    await setActionLog({
      eventType: "BUTTON_CLICK",
      title: "단말기 리스트 엑셀 내보내기 버튼 클릭",
    });
    try {
      setIsSearch(true);

      // 페이징 없이 모든 데이터 조회
      const response = await axiosInstance.get("/api/v1/terminal/list", {
        params: {
          currentPageNo: 1,
          recordsPerPage: INT_MAX,
          ...(deviceType ? { deviceType } : {}),
          ...(vanType ? { vanType } : {}),
          ...(searchTerminalColor ? { terminalColor: searchTerminalColor } : {}),
          ...(searchFirmwareType
            ? { firmwareType: Number(searchFirmwareType) }
            : {}),
          spSerialNo: searchSpSerialNo.trim().toUpperCase() || undefined,
          // selectSerialCode: selectSerialCode,
          ...(tab !== "ALL" ? { terminalStatus: tab } : {}),
        },
      });

      if (response.status === 200) {
        const data = response.data as any;
        const terminalList = data.data.list || [];

        // ExcelJS 워크북 생성
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("단말기 리스트");

        // 컬럼 정의
        worksheet.columns = [
          { header: "번호", key: "number", width: 8 },
          { header: "S/N", key: "spSerialNo", width: 20 },
          { header: "MAC", key: "macAddress", width: 20 },
          { header: "내부모델", key: "internalModelName", width: 18 },
          { header: "외부모델", key: "externalModelName", width: 18 },
          { header: "VAN", key: "vanType", width: 15 },
          { header: "색상", key: "terminalColor", width: 10 },
          { header: "S/N 주입", key: "spInjection", width: 12 },
          { header: "MAC 주입", key: "macInjection", width: 12 },
          { header: "등록날짜", key: "createdDate", width: 12 },
        ];

        // 헤더 스타일링
        worksheet.getRow(1).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF4472C4" },
          };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } },
          };
        });

        // 데이터 추가
        terminalList.forEach((row: any, index: number) => {
          const dataRow = worksheet.addRow({
            number: index + 1,
            spSerialNo: row.spSerialNo || "",
            macAddress: row.macAddress || "",
            internalModelName: row.internalModelName || "",
            externalModelName: row.externalModelName || "",
            vanType:
              vanTypeOptions.find((item) => item.value === row.vanType)
                ?.label || row.vanType,
            terminalColor: row.terminalColor || "",
            spInjection: row.spSerialInjectionYn === "Y" ? "주입" : "미주입",
            macInjection: row.macInjectionYn === "Y" ? "주입" : "미주입",
            createdDate: row.createdDate
              ? dayjs(row.createdDate).format("YYYY-MM-DD")
              : "",
          });

          // 데이터 행 스타일링
          dataRow.eachCell((cell, colNumber) => {
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = {
              top: { style: "thin", color: { argb: "FFD9D9D9" } },
              left: { style: "thin", color: { argb: "FFD9D9D9" } },
              bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
              right: { style: "thin", color: { argb: "FFD9D9D9" } },
            };

            // 주입/미주입 상태에 따른 색상 적용
            const cellValue = cell.value;
            if (
              typeof cellValue === "string" &&
              (cellValue === "주입" || cellValue === "미주입")
            ) {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: {
                  argb: cellValue === "주입" ? "FFD4EDDA" : "FFF8D7DA",
                },
              };
              cell.font = {
                color: { argb: cellValue === "주입" ? "FF155724" : "FFA94442" },
                bold: true,
              };
            }
          });
        });

        // 파일 다운로드
        const fileName = `단말기_리스트_${dayjs().format(
          "YYYY-MM-DD_HH-mm-ss"
        )}.xlsx`;
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        await setActionLog({
          eventType: "EXPORT",
          title: "단말기 리스트 엑셀 내보내기 성공",
          content: `총 ${terminalList.length}건`,
        });
        toast.success(
          `엑셀 파일이 다운로드되었습니다. (${terminalList.length}건)`
        );
      }
    } catch (err: any) {
      await setActionLog({
        eventType: "EXPORT",
        title: "단말기 리스트 엑셀 내보내기 실패",
        content: err,
      });
      toast.error("엑셀 내보내기에 실패하였습니다.");
      console.error("엑셀 내보내기 오류:", err);
    } finally {
      setIsSearch(false);
    }
  };

  const tabList = [
    { label: "전체", value: "ALL" },
    { label: "미주입", value: "1" },
    { label: "주입", value: "2" },
    { label: "출고", value: "3" },
    { label: "반품 미주입", value: "4" },
    { label: "반품 주입", value: "5" },
  ];

  const getInjectionStatus = (yn: string) =>
    yn === "Y" ? "주입" : "미주입";

  const renderInjectBadge = (yn: string) => {
    const status = getInjectionStatus(yn);
    return (
      <Badge
        style={{
          backgroundColor: status === "주입" ? "#d4edda" : "#f8d7da",
          color: status === "주입" ? "#155724" : "#a94442",
        }}
        variant="filled"
      >
        {status}
      </Badge>
    );
  };

  /*
  const filtered = data.filter((row) => {
    const status = getKeyInjectionStatus(row.keyInjectionYn);
    const tabMatch = tab === "All" || status === tab;
    const searchMatch =
      search === "" ||
      String(row.terminalId).includes(search) ||
      String(row.serialNo).includes(search) ||
      String(row.deviceType).includes(search);
    return tabMatch && searchMatch;
  });

  const PAGE_SIZE = 15;

  const emptyRows =
    PAGE_SIZE - filtered.length > 0 ? PAGE_SIZE - filtered.length : 0;

  */

  const handleTerminalView = async (row: any) => {
    await showModal({ component: TerminalView, props: { row } });
  };

  const rows = data.map((row, idx) => {
    const isSelected = selectedRow === row.terminalId;

    return (
      <Table.Tr
        key={row.terminalId}
        bg={
          isSelected
            ? "var(--mantine-color-blue-light)"
            : hoveredRow === idx
            ? "#f0f4fa"
            : undefined
        }
        fw={isSelected ? "700" : undefined}
        onMouseEnter={() => setHoveredRow(idx)}
        onMouseLeave={() => setHoveredRow(null)}
        onClick={() => {
          setSelectedRow((prev) =>
            prev === row.terminalId ? null : row.terminalId,
          );
          void handleTerminalView(row);
        }}
        style={{ cursor: "pointer" }}
      >
        <Table.Td p={"4px 12px"}>{row.terminalId}</Table.Td>
        <Table.Td p={"4px 12px"}>{row.spSerialNo || "-"}</Table.Td>
        <Table.Td p={"4px 12px"}>{row.internalModelName || "-"}</Table.Td>
        <Table.Td p={"4px 12px"}>{row.externalModelName || "-"}</Table.Td>
        <Table.Td p={"4px 12px"}>
          {vanTypeOptions.find((item) => item.value === row.vanType)?.label ||
            "-"}
        </Table.Td>
        <Table.Td p={"4px 12px"}>{row.terminalColor || "-"}</Table.Td>
        <Table.Td p={"4px 12px"}>
          {renderInjectBadge(row.spSerialInjectionYn)}
        </Table.Td>
        <Table.Td p={"4px 12px"}>
          {renderInjectBadge(row.macInjectionYn)}
        </Table.Td>
        <Table.Td p={"4px 12px"}>
          {row.createdDate ? dayjs(row.createdDate).format("YYYY-MM-DD") : ""}
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <div
      style={{
        height: "100%",
        background: "#fafbfc",
        borderRadius: "12px",
        padding: "0.75rem 1.5rem",
        display: "flex",
        flexDirection: "column",
        minHeight: "500px", // 최소 높이 보장
      }}
    >
      <LoadingOverlay
        visible={isSearch}
        zIndex={1000}
        overlayProps={{ radius: "sm", bg: "rgba(0,0,0,0.3)" }}
        loaderProps={{ color: "pink", type: "bars" }}
      />
      <Flex align={"center"} justify={"space-between"} pb={"1rem"}>
        <Box fz={"1.75rem"} fw={700}>
          {menuTitle}
        </Box>
        <Flex gap={"xs"}>
          <Menu
            transitionProps={{ transition: "pop-top-right" }}
            position="bottom-end"
            width={200}
            withinPortal
            radius="md"
          >
            <Menu.Target>
              <Button
                leftSection={
                  <IconPlus
                    size={20}
                    color={theme.white}
                    stroke={1.5}
                  />
                }
                variant="filled"
                color="teal"
                radius="md"
                disabled={!canWrite}
              >
                등록
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>등록</Menu.Label>
              <Menu.Item
                leftSection={
                  <IconFileImport size={20} color="#708D75" stroke={1.5} />
                }
                onClick={handleExcelImport}
              >
                엑셀 업로드
              </Menu.Item>
              <Menu.Item
                leftSection={
                  <IconPlus size={20} color="#0F766E" stroke={1.8} />
                }
                onClick={handleDirectRegister}
              >
                직접 등록
              </Menu.Item>
              <Menu.Divider />
              <Menu.Label>보조 기능</Menu.Label>
              <Menu.Item
                leftSection={
                  <IconFileExport size={20} color="#F59E0B" stroke={1.5} />
                }
                onClick={() => handleExport("register")}
              >
                등록 샘플
              </Menu.Item>

              <Menu.Item
                leftSection={
                  <IconFileExport size={20} color="#F59E0B" stroke={1.5} />
                }
                onClick={() => handleExport("incoming")}
              >
                반품 샘플
              </Menu.Item>
              <Menu.Item
                leftSection={
                  <IconDownload size={20} color="#8FC0CF" stroke={1.5} />
                }
                onClick={handleExcelExport}
              >
                목록 내보내기
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>
      </Flex>

      <Flex align={"flex-end"} gap={"xs"} pb={"1rem"}>
        <TextInput
          label="S/N"
          placeholder="S/N을 입력하세요"
          value={searchSpSerialNo}
          onChange={(e) => {
            setSearchSpSerialNo(e.target.value);
          }}
        />
        {/* <TextInput
          label="안드로이드 S/N"
          placeholder="안드로이드 S/N을 입력하세요"
          value={selectSerialCode}
          onChange={(e) => {
            setSelectSerialCode(e.target.value);
          }}
        /> */}
        <Select
          label="모델"
          data={deviceTypeOptions}
          defaultValue={deviceTypeOptions[0]?.value || ""}
          value={deviceType}
          allowDeselect={false}
          onChange={(_value, option) => setDeviceType(_value || "")}
        />
        <Select
          label="VAN"
          data={vanTypeOptions}
          defaultValue={vanTypeOptions[0]?.value || ""}
          value={vanType}
          allowDeselect={false}
          onChange={(_value, option) => setVanType(_value || "")}
        />
        <Select
          label="색상"
          data={[
            { value: "", label: "전체" },
            { value: "BLACK", label: "BLACK" },
            { value: "WHITE", label: "WHITE" },
          ]}
          value={searchTerminalColor}
          allowDeselect={false}
          onChange={(v) => setSearchTerminalColor(v || "")}
        />
        <Select
          label="펌웨어타입"
          data={[
            { value: "", label: "전체" },
            { value: "1", label: "일반" },
            { value: "2", label: "AOP" },
          ]}
          value={searchFirmwareType}
          allowDeselect={false}
          onChange={(v) => setSearchFirmwareType(v || "")}
        />
        <Button
          leftSection={<IconSearch size={20} stroke={1.5} />}
          onClick={() => {
            if (pageNo !== 1) {
              setPageNo(1);
            } else {
              getList();
            }
          }}
          disabled={isSearch}
          variant="filled"
          style={{ backgroundColor: "#8FC0CF", color: "#fff" }}
        >
          검색
        </Button>
        <div style={{ marginLeft: "auto", display: "flex", gap: "0.5rem" }}>
          <Button
            leftSection={<IconTrash size={20} stroke={1.5} />}
            onClick={async () => {
              const flag = await showModal({
                component: TerminalDelete,
                props: {},
              });
              if (flag) {
                getList();
              }
            }}
            disabled={!canWrite}
            variant="filled"
            color="gray"
          >
            삭제
          </Button>
        </div>
      </Flex>
      {/* Floating progress widget for Excel import */}
      {importGroupId && importProgress > 0 && importProgress < 100 && (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            padding: "10px 12px",
            zIndex: 1000,
            minWidth: 220,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
            엑셀 Import 진행중
          </div>
          <div style={{ fontSize: 12, color: "#555" }}>
            그룹: {importGroupId}
          </div>
          <div
            style={{
              marginTop: 6,
              height: 8,
              background: "#eef2f7",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${Math.max(5, Math.min(100, importProgress))}%`,
                height: "100%",
                background: "#8FC0CF",
              }}
            />
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: "#333" }}>
            {Math.floor(importProgress)}%
          </div>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center" }}>
        {tabList.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            style={{
              background: tab === t.value ? "#fff" : "transparent",
              border: "none",
              borderBottom:
                tab === t.value ? "2px solid #e0eafc" : "2px solid transparent",
              color: "#222b45",
              fontWeight: tab === t.value ? "bold" : "normal",
              fontSize: 16,
              marginRight: 24,
              padding: "8px 0",
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        style={{
          flex: 1, // 남은 공간을 모두 차지
          minHeight: "300px", // 최소 높이 보장
          overflow: "auto",
          scrollbarGutter: "stable",
          boxSizing: "border-box",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          borderRadius: 8,
          background: "#fff",
          position: "relative",
        }}
      >
        <Table>
          <Table.Thead>
            <Table.Tr bg={"#f5f6fa"} ta={"left"} pos={"sticky"} top={0}>
              <Table.Th p={12} pos={"sticky"} top={0}>
                번호
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                S/N
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                내부모델
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                외부모델
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                VAN
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                색상
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                S/N 주입
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                MAC 주입
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0}>
                등록날짜
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows}
            {/*Array.from({ length: 30 }).map((_, i) => (
              <Table.Tr key={i} style={{ height: 30,}}>
                <td colSpan={5}>asd</td>
              </Table.Tr>
            ))*/}
          </Table.Tbody>
        </Table>
      </div>
      <Flex pt={16} align={"flex-end"}>
        <Flex mr={"auto"} align={"center"}>
          <span style={{ marginRight: "1rem" }}>총 {total}개</span>
          <select
            onChange={(e) => {
              const opt_list = [20, 50, 100];
              setLength(opt_list[e.target.options.selectedIndex]);
              //getPageCount()
            }}
            style={{
              height: "31px",
              width: "50px",
              textAlign: "center",
              fontSize: "1rem",
            }}
          >
            <option>20</option>
            <option>50</option>
            <option>100</option>
          </select>
          <span>개씩 보기</span>
        </Flex>
        <ReactPaginate
          previousLabel="<"
          nextLabel=">"
          breakLabel="..."
          pageCount={pageTotal}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={(e) => {
            setPageNo(e.selected + 1);
          }}
          forcePage={pageNo - 1}
          containerClassName="pagination"
          activeClassName="active"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          nextClassName="page-item"
          previousLinkClassName="page-link"
          nextLinkClassName="page-link"
          breakClassName="page-item"
          breakLinkClassName="page-link"
        />
      </Flex>
    </div>
  );
};

export default Terminals;
